/**
 * ==============================================================================
 * useClientScopedData.js - Multi-Tenant Data Fetching Hooks
 * ==============================================================================
 * 
 * This module provides custom React hooks for fetching data with automatic
 * client (tenant) filtering. It's essential for the multi-tenant architecture
 * where admin users can switch between clients while regular users only see
 * their own client's data.
 * 
 * ARCHITECTURE:
 * -------------
 * - Regular users: Automatically filtered by their assigned client_id
 * - Admin users: Can view all data or filter by selected client
 * - Client switching: Triggers automatic refetch of data
 * 
 * HOW IT WORKS:
 * -------------
 * 1. Hook subscribes to AdminClientContext for selectedClientId
 * 2. When selectedClientId changes, data is automatically refetched
 * 3. API calls include ?view_as_client={clientId} parameter for admins
 * 4. Backend applies appropriate filtering based on user role
 * 
 * USAGE:
 * ------
 * // Simple usage with pre-built hooks
 * const { data: populations, loading, refetch } = usePopulations();
 * 
 * // Custom endpoint usage
 * const { data, loading, error } = useClientScopedData('/custom-endpoint');
 * 
 * // With transform function
 * const { data } = useClientScopedData('/items', {
 *   transform: (response) => response.filter(item => item.active)
 * });
 * 
 * AVAILABLE HOOKS:
 * ----------------
 * - useClientScopedData(endpoint, options) - Generic hook for any endpoint
 * - usePopulations() - Fetch populations
 * - useStimuli() - Fetch stimuli
 * - useSimulations() - Fetch simulations
 * - useDashboardStats() - Fetch dashboard statistics
 * 
 * ==============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminClient } from '../context/AdminClientContext';
import api from '../lib/api';

/**
 * useClientScopedData - Generic Client-Scoped Data Fetching Hook
 * --------------------------------------------------------------
 * Fetches data from an API endpoint with automatic client filtering
 * for multi-tenant data isolation.
 * 
 * @param {string} endpoint - The API endpoint to fetch from (e.g., '/populations')
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {Function} options.transform - Optional transform function for response
 * @param {any} options.defaultValue - Default value for data (default: [])
 * 
 * @returns {Object} Hook return value
 * @returns {any} returns.data - The fetched data (or defaultValue)
 * @returns {boolean} returns.loading - Loading state
 * @returns {Error} returns.error - Error object if fetch failed
 * @returns {Function} returns.refetch - Function to refetch data
 * @returns {Function} returns.silentRefetch - Refetch without loading state
 */
export function useClientScopedData(endpoint, options = {}) {
  const { autoFetch = true, transform, defaultValue = [] } = options;
  
  // Get selected client from admin context (for client switching)
  const { selectedClientId } = useAdminClient();
  
  // State management
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  
  // Track initial load to avoid showing loading spinner on refetch
  const isInitialLoad = useRef(true);

  /**
   * Fetch data from the API
   * @param {boolean} silent - If true, don't show loading state
   */
  const fetchData = useCallback(async (silent = false) => {
    // Only show loading on initial load, not on silent refetch
    if (!silent && isInitialLoad.current) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Build URL with optional client filter for admins
      // If selectedClientId is 'all' or null, fetch all data
      const url = selectedClientId && selectedClientId !== 'all'
        ? `${endpoint}?view_as_client=${selectedClientId}`
        : endpoint;
      
      const response = await api.get(url);
      
      // Apply optional transform function
      const finalData = transform ? transform(response.data) : response.data;
      setData(finalData);
      isInitialLoad.current = false;
    } catch (err) {
      console.error(`Failed to fetch ${endpoint}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, selectedClientId, transform]);

  /**
   * Effect: Auto-fetch on mount and when client selection changes
   * This ensures data is always fresh when switching between clients
   */
  useEffect(() => {
    if (autoFetch) {
      isInitialLoad.current = true;
      fetchData();
    }
  }, [endpoint, selectedClientId]); // Re-fetch when endpoint or client changes

  // Silent refetch that doesn't trigger loading state
  const silentRefetch = useCallback(() => fetchData(true), [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    silentRefetch
  };
}

// ==============================================================================
// PRE-BUILT HOOKS FOR COMMON ENDPOINTS
// ==============================================================================

/**
 * usePopulations - Fetch populations with client filtering
 * @returns {Object} { data, loading, error, refetch }
 */
export function usePopulations() {
  return useClientScopedData('/populations');
}

/**
 * useStimuli - Fetch stimuli with client filtering
 * @returns {Object} { data, loading, error, refetch }
 */
export function useStimuli() {
  return useClientScopedData('/stimuli');
}

/**
 * useSimulations - Fetch simulations with client filtering
 * @returns {Object} { data, loading, error, refetch }
 */
export function useSimulations() {
  return useClientScopedData('/simulations');
}

/**
 * useDashboardStats - Fetch dashboard statistics with client filtering
 * @returns {Object} { data, loading, error, refetch }
 */
export function useDashboardStats() {
  return useClientScopedData('/dashboard/stats');
}
